"use client"

import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from "react"
import { createPortal } from "react-dom"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { History, X } from "lucide-react"

import { DISMISS_OVERFLOW_ATTRIBUTE } from "@/components/layout/Toolbar"
import { RecentActivity } from "@/components/research/RecentActivity"
import { useUserActions } from "@/context/UserContext"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { slideIn, slideOut } from "@/lib/cascade"

/*
  Whether the panel is open, as a tiny store.

  The button and the panel are mounted apart: the button in the header — or folded into the phone's
  ⋯, where it unmounts when that closes — and the panel once, for the whole app (see AppHeader).
  The same arrangement as the find and go-to panels.
*/
let open = false
const listeners = new Set<() => void>()
function setOpen(next: boolean) {
  if (next === open) return
  open = next
  listeners.forEach((listener) => listener())
}
function useOpen() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => open,
    () => false,
  )
}

/** The open panel's surface, for `closeHistory` to slide away. */
let panelNode: HTMLElement | null = null
/** Whether the panel is sliding away, so a second close does not start a second slide. */
let closing = false

/**
 * Close the panel the way it opened, in reverse: sliding back down out of sight, then gone. Every way
 * of closing it comes here — its ✕, its button, a press elsewhere, Escape, following a card.
 */
function closeHistory() {
  if (!open || closing) return
  if (!panelNode) return setOpen(false)
  closing = true
  void slideOut(panelNode, "bottom").then(() => {
    closing = false
    setOpen(false)
  })
}

/** Marks the button, so a press on it is not taken for a press outside the panel. */
const TOGGLE_ATTRIBUTE = "data-history-toggle"

/**
 * How long a press will wait for a refreshed list before opening anyway.
 *
 * The panel used to fetch as it rose: `RecentActivity` calls `ensureActivity` when it mounts,
 * and the panel mounts on the press — so a list more than 30 seconds old slid up stale and then
 * visibly rewrote itself a moment later, which is the one thing a history panel must not do.
 *
 * So the press starts the refresh and waits for it. The cap is what keeps that from turning a
 * button into a pause: past this the panel opens on what is held and updates in place, which is
 * the old behaviour and the right fallback for a slow connection. A fresh list resolves on the
 * next microtask, so the common press is not delayed at all.
 */
const REFRESH_BEFORE_OPEN_MS = 250

/** The header's history button. Flips the store, and makes sure the list is current first. */
export function HistoryButton() {
  const t = useTranslations("nav.History")
  const isOpen = useOpen()
  const actions = useUserActions()

  /*
    Warm it on approach. `ensureActivity` is a no-op on a fresh list and de-duplicates itself
    while one request is in flight, so this costs nothing when there is nothing to do — and by
    the time a pointer that has crossed the button arrives at a press, the answer is usually
    already in the store and the panel opens on a current list with no wait at all.

    `focus` as well, for the keyboard: a button reached by Tab gets the same head start as one
    reached by pointer.
  */
  const warm = () => void actions.ensureActivity()

  const onClick = () => {
    if (isOpen) return closeHistory()
    void Promise.race([
      actions.ensureActivity(),
      new Promise((resolve) => setTimeout(resolve, REFRESH_BEFORE_OPEN_MS)),
    ]).then(() => setOpen(true))
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          {...{ [TOGGLE_ATTRIBUTE]: "", [DISMISS_OVERFLOW_ATTRIBUTE]: "" }}
          variant={isOpen ? "default" : "secondary"}
          size="icon"
          aria-pressed={isOpen}
          aria-label={t("open")}
          onPointerEnter={warm}
          onFocus={warm}
          onClick={onClick}
        >
          <History />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{t("open")}</TooltipContent>
    </Tooltip>
  )
}

/**
 * The reader's recent work, from anywhere in the app: a panel rising from the bottom of the screen
 * with the same card row as the foot of the research canvas — the same component, not a copy, so
 * the two never describe one thing two ways.
 *
 * ── Not a sheet ──────────────────────────────────────────────────────────────────────────
 *
 * It was a modal sheet, which on opening locks the page's scrolling, hides everything outside it
 * from the rest of the page, and traps focus — work over the whole page, which on a treaty is a
 * large one — and fetched its cards only once open. This is a plain panel over the page: it slides
 * up on the compositor, the cards are already in the user store (fetched once the app settled; see
 * context/UserContext, and HistoryButton, which refreshes them before the panel rises rather
 * than as it does), and nothing else on the page is touched.
 *
 * Out of the way when it has done its job: a press anywhere else, Escape, or following a card.
 */
export function HistoryPanel() {
  const t = useTranslations("nav.History")
  const isOpen = useOpen()
  const panel = useRef<HTMLDivElement>(null)
  const pathname = usePathname()

  // Up from the bottom edge as it opens; known to `closeHistory`, which slides it back down.
  useLayoutEffect(() => {
    const surface = panel.current
    if (!isOpen || !surface) return
    slideIn(surface, "bottom")
    panelNode = surface
    return () => {
      if (panelNode === surface) panelNode = null
    }
  }, [isOpen])

  // A card followed has done its job.
  useEffect(() => closeHistory(), [pathname])

  useEffect(() => {
    if (!isOpen) return
    const close = closeHistory
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target || panel.current?.contains(target)) return
      if (target.closest(`[${TOGGLE_ATTRIBUTE}]`)) return
      // Something floating from elsewhere — a tooltip, a popover — is not the page.
      if (target.closest("[data-radix-popper-content-wrapper]")) return
      close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [isOpen])

  if (!isOpen) return null

  return createPortal(
    <div
      ref={panel}
      // Over the page and its floating controls; below the popovers and tooltips its own controls open.
      className="fixed inset-x-0 bottom-0 z-40 flex flex-col gap-4 rounded-t-4xl bg-background pt-6 shadow-[0_0px_20px_rgba(0,0,0,0.1)]"
      // Clear of a phone's home indicator.
      style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}
    >
      {/*
        \`px-6\`, the gutter \`RecentActivity\` carries, so the heading and the first card start on
        the same line.
      */}
      <div className="flex flex-row items-center justify-between gap-4 px-6">
        <h2 className="text-lg font-semibold md:text-xl">{t("title")}</h2>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="secondary" size="icon" aria-label={t("close")} onClick={closeHistory}>
              <X />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("close")}</TooltipContent>
        </Tooltip>
      </div>

      <RecentActivity
        emptyState={<p className="px-6 pb-2 text-sm font-normal text-muted-foreground">{t("empty")}</p>}
        /*
          Only before the store's first fetch has answered — rare, as it is made once the app
          settles. Placeholders at the card row's own dimensions (124px cards, a 12px gap, the 32px
          control row under them), so the panel opens at the height it will keep.
        */
        loadingState={
          <div aria-hidden className="w-full px-6">
            <div className="flex flex-row gap-3 overflow-hidden">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-[124px] w-[400px] shrink-0 rounded-2xl" />
              ))}
            </div>
            <div className="mt-3 flex h-8 items-center">
              <Skeleton className="h-8 w-28 rounded-full" />
            </div>
          </div>
        }
      />
    </div>,
    document.body,
  )
}
